"use client";

import type { InstantAssistantContextCard, InstantAssistantMessage } from "@/types/assistant";

const DB_NAME = "instant-assistant";
const DB_VERSION = 1;
const STORE_TRANSCRIPTS = "transcripts";
const STORE_DRAFTS = "drafts";
const DEFAULT_USER_SCOPE = "anonymous";

export type AssistantDraftState = {
  text: string;
  context: InstantAssistantContextCard[];
};

function isBrowser() {
  return typeof window !== "undefined" && typeof indexedDB !== "undefined";
}

function openDatabase(): Promise<IDBDatabase> {
  if (!isBrowser()) {
    return Promise.reject(new Error("IndexedDB is unavailable"));
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_TRANSCRIPTS)) {
        db.createObjectStore(STORE_TRANSCRIPTS);
      }
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        db.createObjectStore(STORE_DRAFTS);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  handler: (store: IDBObjectStore) => Promise<T>,
): Promise<T | null> {
  try {
    const db = await openDatabase();
    return await new Promise<T>((resolve, reject) => {
      const txn = db.transaction(storeName, mode);
      const store = txn.objectStore(storeName);

      handler(store)
        .then(resolve)
        .catch(reject);

      txn.oncomplete = () => db.close();
      txn.onerror = () => reject(txn.error ?? new Error("Transaction failed"));
    });
  } catch (error) {
    console.warn("InstantAssistant storage unavailable", error);
    return null;
  }
}

function buildStorageKey(namespaceId: string, userId?: string | null) {
  const normalizedUser = userId?.trim();
  const scope = normalizedUser && normalizedUser.length > 0 ? normalizedUser : DEFAULT_USER_SCOPE;
  return `${scope}::${namespaceId}`;
}

export async function persistTranscript(
  namespaceId: string,
  messages: InstantAssistantMessage[],
  userId?: string | null,
): Promise<void> {
  const key = buildStorageKey(namespaceId, userId);
  await withStore(STORE_TRANSCRIPTS, "readwrite", async (store) => {
    store.put(messages, key);
    return Promise.resolve();
  });
}

export async function readTranscript(
  namespaceId: string,
  userId?: string | null,
): Promise<InstantAssistantMessage[] | null> {
  const key = buildStorageKey(namespaceId, userId);
  const result = await withStore<InstantAssistantMessage[]>(STORE_TRANSCRIPTS, "readonly", async (store) => {
    return await new Promise<InstantAssistantMessage[]>((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve((request.result as InstantAssistantMessage[]) ?? []);
      request.onerror = () => reject(request.error ?? new Error("Failed to read transcript"));
    });
  });

  return result ?? null;
}

export async function clearTranscript(namespaceId: string, userId?: string | null): Promise<void> {
  const key = buildStorageKey(namespaceId, userId);
  await withStore(STORE_TRANSCRIPTS, "readwrite", async (store) => {
    store.delete(key);
    return Promise.resolve();
  });
}

export async function persistDraft(
  namespaceId: string,
  draft: AssistantDraftState,
  userId?: string | null,
): Promise<void> {
  const key = buildStorageKey(namespaceId, userId);
  await withStore(STORE_DRAFTS, "readwrite", async (store) => {
    store.put(draft, key);
    return Promise.resolve();
  });
}

export async function readDraft(
  namespaceId: string,
  userId?: string | null,
): Promise<AssistantDraftState | null> {
  const key = buildStorageKey(namespaceId, userId);
  const result = await withStore<AssistantDraftState | string>(STORE_DRAFTS, "readonly", async (store) => {
    return await new Promise<AssistantDraftState | string>((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve((request.result as AssistantDraftState | string | undefined) ?? "");
      request.onerror = () => reject(request.error ?? new Error("Failed to read draft"));
    });
  });

  if (typeof result === "string") {
    return { text: result, context: [] };
  }

  return result ?? null;
}

export async function clearDraft(namespaceId: string, userId?: string | null): Promise<void> {
  const key = buildStorageKey(namespaceId, userId);
  await withStore(STORE_DRAFTS, "readwrite", async (store) => {
    store.delete(key);
    return Promise.resolve();
  });
}
